// Wraps a <details>: slides its content open/closed and flags `data-open` right away so the caret
// turns with the panel. Opening one closes the others in the same section, so only one is open at a time.
// Without JS (or with reduced motion) the plain <details> toggle still works.
if (!customElements.get('accordion-details')) {
  // Not top-level consts: this file can load more than once per page
  const SIZE_PROPS = ['height', 'paddingTop', 'paddingBottom', 'marginTop', 'marginBottom'];
  const CLOSED = Object.fromEntries(SIZE_PROPS.map((prop) => [prop, '0px']));

  customElements.define(
    'accordion-details',
    class AccordionDetails extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details');
        this.summary = this.details.querySelector('summary');
        this.content = this.summary.nextElementSibling;
        this.toggleAttribute('data-open', this.details.open);
        this.summary.addEventListener('click', this.onClick.bind(this));
      }

      onClick(event) {
        const opening = !this.details.open || !this.hasAttribute('data-open');
        if (opening) this.closeOthers();
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          requestAnimationFrame(() => this.toggleAttribute('data-open', this.details.open));
          return;
        }
        event.preventDefault();
        this.toggle(opening);
      }

      closeOthers() {
        const scope = this.closest('.shopify-section') || document;
        for (const other of scope.querySelectorAll('accordion-details[data-open]')) {
          if (other !== this) other.close();
        }
      }

      close() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.details.open = false;
          this.removeAttribute('data-open');
          return;
        }
        this.toggle(false);
      }

      toggle(opening) {
        // Start from the current size so a click mid-animation reverses smoothly
        const from = this.details.open ? this.size() : CLOSED;
        this.animation?.cancel();

        this.toggleAttribute('data-open', opening);
        this.details.open = true;
        const to = opening ? this.size() : CLOSED;

        this.content.style.overflow = 'hidden';
        // Padding and margin collapse too, or the panel stops short and snaps shut;
        // fill keeps the last frame until <details> closes, so it never flashes open
        this.animation = this.content.animate(
          [
            { ...from, opacity: opening ? 0 : 1 },
            { ...to, opacity: opening ? 1 : 0 },
          ],
          { duration: 300, easing: 'cubic-bezier(0.25, 1, 0.5, 1)', fill: 'forwards' }
        );
        this.animation.onfinish = () => {
          this.details.open = opening;
          this.content.style.overflow = '';
          this.animation.cancel();
          this.animation = null;
        };
      }

      size() {
        const style = getComputedStyle(this.content);
        return Object.fromEntries(SIZE_PROPS.map((prop) => [prop, style[prop]]));
      }
    }
  );
}
